import React, { useState, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  setExcelData,
  setIsExcelValid,
  setValidationErrors,
} from "../config/empolyeeSlice";
import { useDropzone } from "react-dropzone";
import { Controller } from "react-hook-form";
import { Button, Card, Spinner } from "react-bootstrap";
import { FileText, Trash, Upload } from "react-bootstrap-icons";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
const REQUIRED_FIELDS = [
  "First Name (English)",
  "Family Name (English)",
  "First Name  (Arabic)",
  "Family Name (Arabic)",
  "DOB",
  "Nationality",
  "Mobile No",
  "Email",
  "National Id",
  "Branch",
  "System Privilage",
  "Password",
];

const DATA_REQUIRED_FIELDS = [
  "First Name (English)",
  "Family Name (English)",
  "Email",
  "National Id",
  "System Privilage",
  "Password",
];

const EmpolyeeUploadCard = ({
  control,
  watch,
  errors,
  setValue,
  name = "excelFile",
}) => {
  const dispatch = useDispatch();

  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const allBranches = JSON.parse(localStorage.getItem("branch_settings")) || [];
  const validateHeaders = (rawHeaders) =>
    REQUIRED_FIELDS.every((field) => rawHeaders.includes(field));

  const validateFirstRow = (row) =>
    REQUIRED_FIELDS.every((field) => row[field]?.toString().trim() !== "");

  const validateExcelData = (jsonData) => {
    const errors = [];

    const isEnglish = (value) => /^[A-Za-z\s'-]+$/.test(value);
    const isArabic = (text) =>
      /^[\u0600-\u06FF\s\u064B-\u0652\u0670\u0671\u067E\u0686\u06A4\u06AF'-]+$/.test(
        text
      );
    const isValidDate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(value);
    const allowedPrivileges = ["Employee", "Admin", "Super Admin"];
    jsonData.forEach((row, index) => {
      const rowNum = index + 2;
      const rowErrors = [];

      // ✅ Required fields
      if (!row["First Name (English)"]) {
        rowErrors.push({
          row: rowNum,
          message: "First Name (English) is required",
        });
      } else if (!isEnglish(row["First Name (English)"])) {
        rowErrors.push({
          row: rowNum,
          message: "First Name (English) must be in English",
        });
      }

      if (!row["Family Name (English)"]) {
        rowErrors.push({
          row: rowNum,
          message: "Family Name (English) is required",
        });
      } else if (!isEnglish(row["Family Name (English)"])) {
        rowErrors.push({
          row: rowNum,
          message: "Family Name (English) must be in English",
        });
      }

      if (!row["Nationality"]) {
        rowErrors.push({ row: rowNum, message: "Nationality is required" });
      }

      if (!row["Email"]) {
        rowErrors.push({ row: rowNum, message: "Email is required" });
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row["Email"])) {
        rowErrors.push({ row: rowNum, message: "Invalid Email format" });
      }

      if (!row["National Id"]) {
        rowErrors.push({ row: rowNum, message: "National Id is required" });
      } else {
        const nationality = row["Nationality"];

        const nationalIdRaw = row["National Id"] || row["Passport No"] || "";
        const nationalId = String(nationalIdRaw).trim(); // ✅ always a string

        if (nationality === "SAU") {
          if (!/^\d{10}$/.test(nationalId) || !nationalId.startsWith("1")) {
            rowErrors.push({
              row: rowNum,
              message: "Saudi National ID must be 10 digits and start with 1",
            });
          }
        } else {
          if (!/^\d{10}$/.test(nationalId) || nationalId.startsWith("1")) {
            rowErrors.push({
              row: rowNum,
              message:
                "Non-Saudi National ID must be 10 digits and must not start with 1",
            });
          }
        }
      }

      if (!row["System Privilage"]) {
        rowErrors.push({
          row: rowNum,
          message: "System Privilage is required",
        });
      } else if (!allowedPrivileges.includes(row["System Privilage"])) {
        rowErrors.push({
          row: rowNum,
          message: `System Privilage must be one of: ${allowedPrivileges.join(
            ", "
          )}`,
        });
      }

      if (!row["Password"]) {
        rowErrors.push({ row: rowNum, message: "Password is required" });
      } else if (String(row["Password"]).length < 8) {
        rowErrors.push({
          row: rowNum,
          message: "Password must be at least 8 characters long",
        });
      }

      // ✅ Optional fields
      if (row["First Name (Arabic)"] && !isArabic(row["First Name (Arabic)"])) {
        rowErrors.push({
          row: rowNum,
          message: "First Name (Arabic) must be in Arabic",
        });
      }

      if (
        row["Family Name (Arabic)"] &&
        !isArabic(row["Family Name (Arabic)"])
      ) {
        rowErrors.push({
          row: rowNum,
          message: "Family Name (Arabic) must be in Arabic",
        });
      }

      if (row["DOB"] && !isValidDate(row["DOB"])) {
        rowErrors.push({
          row: rowNum,
          message: "DOB must be in YYYY-MM-DD format",
        });
      }

      if (row["Branch"]) {
        const branchValue = row["Branch"];

        // Check if the format is valid: comma-separated numbers only
        if (!/^(\d+\s*)(,\s*\d+\s*)*$/.test(branchValue)) {
          rowErrors.push({
            row: rowNum,
            message: "Branch must be a comma-separated list of numbers only",
          });
        } else {
          const branchIds = branchValue
            ?.split(",")
            ?.map((id) => parseInt(id.trim(), 10))
            ?.filter((id) => !isNaN(id));

          const validBranchIds = allBranches.map((b) =>
            parseInt(b.branch_cchino, 10)
          );

          const invalidIds = branchIds.filter(
            (id) => !validBranchIds.includes(id)
          );

          if (invalidIds.length > 0) {
            rowErrors.push({
              row: rowNum,
              message: `Invalid Branch CCNHI(s): ${invalidIds.join(", ")}`,
            });
          }
        }
      }

      errors.push(...rowErrors);
    });

    return errors;
  };

  const onDrop = useCallback(
    (acceptedFiles) => {
      const excelFile = acceptedFiles[0];
      if (!excelFile) return;

      setIsUploading(true);
      setFile(null);
      dispatch(setExcelData([]));
      dispatch(setValidationErrors([]));
      dispatch(setIsExcelValid(false));

      const reader = new FileReader();

      reader.onload = (e) => {
        let isValid = true;

        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];

          // Get raw headers from row 1
          const rawHeaderRows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
          const headers = rawHeaderRows[0] || [];

          if (!headers.length) {
            toast.error("Excel file is Empty");
            isValid = false;
            return;
          }

          // Validate headers explicitly
          if (!validateHeaders(headers)) {
            toast.error("Excel file is not valid");
            isValid = false;
            return;
          }

          // Now parse full data with header mapping
          const rawJson = XLSX.utils.sheet_to_json(sheet);

          const jsonData = rawJson.map((row, i) => ({
            id: `${Date.now()}-${i + 1}`,
            ...row,
          }));

          if (jsonData.length === 0) {
            toast.error("Excel file is Empty");
            isValid = false;
            return;
          }

          // Optional: validate first row content if needed
          if (!validateFirstRow(jsonData[0])) {
            toast.error("Row 2 has missing required fields.");
            isValid = false;
            return;
          }

          const errors = validateExcelData(jsonData);

          dispatch(setExcelData(jsonData));
          dispatch(setValidationErrors(errors));
          setFile(excelFile);
          setValue(name, excelFile);
          dispatch(setIsExcelValid(true));
        } catch (error) {
          console.error("Error reading Excel file:", error);
          toast.error("Error processing Excel file.");
          dispatch(
            setValidationErrors([{ row: 0, message: "Invalid file format." }])
          );
          isValid = false;
        } finally {
          setIsUploading(false);

          if (!isValid) {
            dispatch(setExcelData([]));
            dispatch(setValidationErrors([]));
            setFile(null);
            dispatch(setIsExcelValid(false));
          }
        }
      };

      reader.readAsArrayBuffer(excelFile);
    },
    [setExcelData, setValidationErrors, setValue, name, setIsExcelValid]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
    },
    maxFiles: 1,
  });

  const handleRemove = () => {
    setFile(null);
    dispatch(setExcelData([]));
    dispatch(setValidationErrors([]));
    dispatch(setIsExcelValid(false));
    setValue(name, null);
  };

  useEffect(() => {
    if (watch("excelFile")) {
      setFile(watch("excelFile"));
    }
  }, []);
  return (
    <Controller
      name={name}
      control={control}
      rules={{ required: "Excel file is required" }}
      render={({ fieldState: { error } }) => (
        <Card className="mt-3">
          <Card.Body>
            <div {...getRootProps({ className: "dropzone-area py-4" })}>
              <input {...getInputProps()} />
              <div className="text-center">
                {isUploading ? (
                  <div className="py-4">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 mb-0">Processing Excel file...</p>
                  </div>
                ) : (
                  <>
                    <div
                      className="rounded-circle bg-success text-white d-flex align-items-center justify-content-center mx-auto mb-3"
                      style={{ width: 60, height: 60 }}
                    >
                      <Upload size={24} />
                    </div>
                    {isDragActive ? (
                      <p>Drop the Excel file here</p>
                    ) : (
                      <>
                        <h5 className="fw-bold">Upload Your Excel File</h5>
                        <p className="mb-3">
                          Drag and drop your .xlsx spreadsheet here
                        </p>
                        <Button variant="success">Choose File</Button>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>

            {file && (
              <div className="mt-4">
                <div className="d-flex align-items-center justify-content-between p-3 border rounded">
                  <div className="d-flex align-items-center">
                    <div
                      className="rounded  d-flex align-items-center justify-content-center"
                      style={{ width: 40, height: 40 }}
                    >
                      <FileText className="text-primary" />
                    </div>
                    <div className="ms-3">
                      <h6 className="mb-0">{file.name}</h6>
                      <small className="text-muted">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </small>
                    </div>
                  </div>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={handleRemove}
                  >
                    <Trash size={16} />
                  </Button>
                </div>
              </div>
            )}

            {error && (
              <div className="text-danger mt-2">
                {error.message || "Excel file is required"}
              </div>
            )}
          </Card.Body>
        </Card>
      )}
    />
  );
};

export default EmpolyeeUploadCard;
